import React from 'react';
import { LegalEnTete, LegalSection, MailLien, P, UL } from './legalShared';

/**
 * Politique de confidentialité.
 *
 * Deux règles d'écriture, dans cet ordre.
 *
 * 1. ON S'EN TIENT AU MINIMUM LÉGAL (articles 13 et 14 du RGPD) : qui est responsable, pour quoi faire,
 *    quelles catégories de données, qui d'autre y a accès, combien de temps, quels droits. Emeni, 18/09 :
 *    une version longue et détaillée n'apporte rien, et donne à lire un règlement intérieur à un
 *    restaurateur. Ce qui n'est pas exigé ne figure pas ici.
 *
 * 2. CE QUI EST ÉCRIT EST VRAI. Chaque sous-traitant nommé correspond à un appel réseau réel du serveur,
 *    chaque durée à un code de purge existant. Le seul traitement par un modèle d'IA est celui de la conseillère
 *    virtuelle (module facultatif) ; il est exécuté chez Cloudflare, déjà nommé en section 3.
 */
export const PrivacyPolicy = () => {
    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <LegalEnTete titre="Politique de confidentialité" autreLien="/legal" autreLabel="Mentions légales" />

            <LegalSection titre="1. Qui décide, qui exécute">
                <P>
                    <strong>L'établissement décide.</strong> Le restaurant ou le prestataire qui ouvre un compte
                    choisit quelles informations il enregistre et à quoi elles lui servent. C'est lui le responsable
                    de ces données, et c'est à lui que ses salariés, ses clients, ses convives et ses fournisseurs
                    doivent s'adresser pour consulter, corriger ou faire effacer ce qui les concerne.
                </P>
                <P>
                    <strong>L'IAmani exécute.</strong> La plateforme range, calcule et envoie ce que l'établissement
                    lui demande, et rien d'autre : c'est un sous-traitant au sens de l'article 28 du RGPD. L'éditeur
                    n'utilise ces données pour aucun autre compte que celui de l'établissement, et ne les montre à
                    aucun autre établissement.
                </P>
            </LegalSection>

            <LegalSection titre="2. Ce qui est collecté, et pourquoi">
                <P>Uniquement ce qui fait fonctionner le service, selon les modules activés :</P>
                <UL>
                    <li>
                        <strong>Accès aux comptes</strong> — identifiant et adresse électronique. Le mot de passe, lui,
                        n'est jamais enregistré : seule une empreinte irréversible l'est, que personne ne peut relire,
                        pas même l'éditeur. Un mot de passe oublié se remplace, il ne se retrouve pas.
                    </li>
                    <li>
                        <strong>Gestion du personnel</strong> — identité et coordonnées des salariés, contrat, planning,
                        absences et rémunération.
                    </li>
                    <li>
                        <strong>Relation commerciale</strong> — identité et coordonnées des clients, contacts,
                        fournisseurs et prestataires, devis, factures et documents échangés.
                    </li>
                    <li>
                        <strong>Organisation d'un événement</strong> — identité des convives, choix de plat, place à
                        table, et les contraintes alimentaires qu'ils déclarent eux-mêmes pour que la cuisine puisse en
                        tenir compte.
                    </li>
                    <li>
                        <strong>Envois automatiques par courriel</strong> — facultatifs et désactivés par défaut :
                        lorsqu'un établissement les active, la plateforme envoie des documents (planning, heures des
                        salariés, factures, absences, réservations, événements, copie de commandes) en pièce jointe ou
                        dans le message, aux seules adresses que l'établissement a lui-même enregistrées. Il lui
                        appartient de n'y indiquer que des destinataires de confiance, comme son comptable.
                    </li>
                    <li>
                        <strong>Conseillère virtuelle (module facultatif)</strong> — lorsqu'un établissement l'installe sur
                        son site ou sa page Ma Carte, les messages que le visiteur échange avec l'assistante virtuelle (IA)
                        sont enregistrés, ainsi que les informations qu'il donne lui-même pour obtenir un devis ou un
                        rendez-vous (nom, adresse électronique, téléphone, adresse d'intervention). Le visiteur est informé
                        qu'il s'adresse à une IA ; l'établissement, responsable de ces données, doit l'informer que la
                        conversation est enregistrée, pour traiter sa demande et assurer le suivi et la qualité du service.
                    </li>
                    <li>
                        <strong>Sécurité du service</strong> — adresse IP et journal des connexions, pour détecter les
                        abus.
                    </li>
                    <li>
                        <strong>Preuve d'acceptation des conditions d'utilisation</strong> — identité du compte, version
                        acceptée, date, adresse IP et type de navigateur, pour pouvoir démontrer cette acceptation en cas
                        de litige.
                    </li>
                </UL>
                <P>
                    Ces données ne sont ni vendues, ni cédées, ni utilisées à des fins publicitaires. Aucune décision
                    automatisée n'est prise à partir d'elles : la conseillère virtuelle ne valide rien à la place du visiteur
                    (chaque étape se confirme) ni de l'établissement, qui peut reprendre la conversation à tout moment.
                </P>
            </LegalSection>

            <LegalSection titre="3. Qui d'autre y a accès">
                <P>
                    Des prestataires techniques interviennent, chacun pour une seule tâche et sur les seules données
                    qu'elle exige :
                </P>
                <UL>
                    <li><strong>Cloudflare</strong> (États-Unis) — hébergement de l'application, de la base de données et des fichiers déposés, et exécution du modèle de langage de la conseillère virtuelle (Workers AI) sur les messages échangés avec elle.</li>
                    <li><strong>Resend</strong> (États-Unis) — envoi des courriels.</li>
                    <li><strong>Stripe</strong> et <strong>Revolut Business</strong> — liens de paiement en ligne. L'établissement raccorde son propre compte : ni la plateforme ni l'éditeur ne détiennent les fonds ou les coordonnées bancaires du payeur.</li>
                    <li><strong>OpenRouteService</strong> (Allemagne) — calcul des distances de déplacement, à partir d'une adresse sans nom associé.</li>
                    <li><strong>Base Adresse Nationale</strong> (service public français) — suggestions d'adresses pendant la saisie.</li>
                </UL>
                <P>
                    Cloudflare, Resend et Stripe étant établis aux États-Unis, ces transferts hors Union européenne
                    reposent sur les clauses contractuelles types de la Commission européenne.
                </P>
            </LegalSection>

            <LegalSection titre="4. Combien de temps">
                <UL>
                    <li>Les données d'un établissement sont conservées tant qu'il utilise le service, puis effacées sur sa demande.</li>
                    <li>L'identité d'un salarié archivé et ses documents sont effacés au plus tard 36 mois après son archivage.</li>
                    <li>Les journaux de connexion sont effacés au bout de 180 jours.</li>
                    <li>Les conversations avec la conseillère virtuelle sont supprimées 90 jours après leur clôture ; l'établissement peut aussi en supprimer une à tout moment.</li>
                    <li>Les preuves d'acceptation des conditions d'utilisation sont conservées 5 ans après l'acceptation.</li>
                </UL>
            </LegalSection>

            <LegalSection titre="5. Sécurité">
                <P>
                    Les échanges se font en HTTPS, les données les plus sensibles — rémunérations, clés de paiement —
                    sont chiffrées en base, les tentatives de connexion sont limitées, et chaque établissement est
                    cloisonné : aucune requête ne peut renvoyer les données d'un autre.
                </P>
            </LegalSection>

            <LegalSection titre="6. Traceurs">
                <P>
                    Aucun cookie publicitaire, aucune mesure d'audience. Le navigateur ne conserve que ce qui est
                    nécessaire au fonctionnement — la session ouverte et quelques préférences d'affichage — et cela
                    reste sur l'appareil.
                </P>
            </LegalSection>

            <LegalSection titre="7. Vos droits">
                <P>
                    Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de
                    portabilité.
                </P>
                <P>
                    Si vous êtes salarié, client, convive ou fournisseur d'un établissement, adressez votre demande à{' '}
                    <strong>cet établissement</strong> : c'est lui qui détient vos données et qui en décide. Pour une
                    question portant sur la plateforme elle-même : <MailLien adresse="contact@l-iamani.com" />.
                </P>
                <P>
                    Vous pouvez aussi saisir la Commission nationale de l'informatique et des libertés (CNIL) —{' '}
                    <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-content hover:text-brand-600 transition-colors">www.cnil.fr</a>.
                </P>
            </LegalSection>
        </div>
    );
};
